<template>
  <div>
    <filters
      v-if="!hideFilters"
      :defaults="filterDefaults"
      @updateDateFrom="updateFilterData('dateFrom', $event)"
      @updateDateTo="updateFilterData('dateTo', $event)"
      @updateSelectedHighway="updateFilterData('highwayId', $event)"
      @updateSelectedJobType="updateFilterData('jobTypeId', $event)"
      @updateSelectedRevi="updateFilterData('revi', $event)"
    />

    <catalog-table
      ref="sinistersTable"
      :table-data="roadSinisters"
      :pagination-info="paginationInfo"
      :tableHeaders="roadSinisterHeaders"
      :tableFilters="roadSinisterFilters"
      :propsToSearch="roadSinisterSearchableProps"
      :actions="roadSinisterActions"
      :loading="loading"
      :loading-content-props="loadingProps"
      :dummy-search="true"
      :page-size="currentPageSize"
      @pageUpdated="currentPage = $event"
      @updatedSearchQuery="filterData.searchQuery = $event"
      @updatedPageSize="currentPageSize = $event"
      v-bind="tableProps"
    >
      <div slot="header" v-if="showTableActions">
        <div style="padding: 8px 0;">
          <md-button
            class="md-success"
            @click="addRoadSinister"
            v-if="$canAdd('Roads', 'sinisterroads')"
          >
            <md-icon class="material-icons">add</md-icon>Agregar
          </md-button>
          <!-- :TODO: Set download report permission -->
          <md-button
            class="md-info"
            @click="downloadReport"
            v-if="$can('download_report', 'Sinister', 'sinister')"
          >
            <md-icon class="fa fa-file-excel"></md-icon>Descargar reporte
          </md-button>
          <md-button
            v-if="$can('update_advances_status_crol', 'Roads', 'sinisterroads')"
            class="md-warning"
            @click="updateCrolAdvances"
          >
            <md-icon style="margin-right: 8px">update</md-icon>Actualizar estatus anticipos CROL
          </md-button>
        </div>
      </div>

      <sinister-dates
        slot="sinisterDates-cell"
        slot-scope="{ item, value }"
        :dateOccurred="value.dateOccurred"
        :dateAssignment="value.dateAssignment"
      />

      <div slot="processName-cell" slot-scope="{ item, value }" class="text-left">
        <div class="md-list-action md-round macro-indicator-status" :class="sinisterClass(item)"></div>
        {{ value }}
        <div v-if="item.urgent || item.priority">
          <md-icon
            style="margin-bottom: 0.3em"
            :class="item.urgent ? 'urgent-style' : 'priority-style'"
          >warning</md-icon>
          <span
            class="md-body-1"
            :class="item.urgent ? 'urgent-style' : 'priority-style'"
          >{{ item.urgent ? "Siniestro urgente" : "Siniestro prioritario" }}</span>
        </div>
      </div>
    </catalog-table>
  </div>
</template>

<script>
import sinisterService from "@/services/sinister";
import { CatalogTable } from "@/components/";
import SinisterDates from "./SinisterDates";
import Filters from "./Filter";
import usesAccountInfoMixin from "@/mixins/usesAccountInfo";
import { downloadFile } from "@/utils";
import { mapState } from "vuex";
import { mapPermissions } from "@/plugins/accountPermissions";
import moment from "moment";

import { debounce } from "lodash";

export default {
  name: "RoadSinisters",

  props: {
    process: {
      type: String,
      required: false,
      default: null,
    },

    processSince: {
      type: Boolean,
      default: false,
    },

    dateFilter: {
      type: String,
      default: "assignationDate",
    },

    filterDefaults: {
      type: Object,
      default: () => ({}),
    },

    showTableActions: {
      type: Boolean,
      default: true,
    },
    hideFilters: {
      type: Boolean,
      default: false,
    },
    tableProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  components: {
    CatalogTable,
    Filters,
    SinisterDates,
  },

  mixins: [usesAccountInfoMixin],

  data() {
    return {
      filterData: {
        dateFrom: null,
        dateTo: null,
        highwayId: null,
        jobTypeId: null,
        revi: null,
        searchQuery: null,
        ...this.filterDefaults,
      },
      currentPage: 1,
      currentPageSize: 10,
      paginationInfo: null,
      loading: null,
      roadSinisters: [],
      currentRoadSinister: {},
      roadSinisterHeaders: {
        sinisterNumber: "Siniestro",
        folioJB: "Folio JB",
        affectedRequisitionNumber: "Requisición",
        sinisterDates: "Fechas",
        sinisterUserCostName: "ARe",
        userReViName: "Residente",
        processName: "Status",
      },
      roadSinisterFilters: {
        folioJB: (folio) => (folio && folio) || "Sin folio",
      },
      roadSinisterSearchableProps: [
        "sinisterNumber",
        "affectedRequisitionNumber",
      ],
      roadSinisterActions: [
        {
          icon: "visibility",
          exe: this.goToSinisterFile,
          tooltip: "Ver Ficha",
        },
        {
          icon: "edit",
          exe: this.editRoadSinister,
          ifUserCan: {
            change: "Roads/sinisterroads",
          },
          tooltip: "Editar",
        },
      ],
      loadingProps: {
        emptyLabel: "No hay siniestros que mostrar.",
        emptyDescription: "Intente más tarde o redefina su consulta.",
      },
    };
  },

  computed: {
    fetchParams() {
      const filtersParams = this.filtersParams;
      const searchQuery = this.filterData.searchQuery;

      return { ...filtersParams, searchQuery };
    },
    filtersParams() {
      const page = this.currentPage;
      const pageSize = this.currentPageSize;
      const from = this.filterData.dateFrom;
      const to = this.filterData.dateTo;
      const highway = this.filterData.highwayId;
      const job = this.filterData.jobTypeId;
      const revi = this.filterData.revi;
      const process = this.process;
      const { processSince } = this;
      const filterAddDate = this.dateFilter !== "assignationDate";

      return {
        page,
        pageSize,
        from,
        to,
        highway,
        job,
        revi,
        process,
        processSince,
        filterAddDate,
      };
    },
    processesById() {
      const byId = {};

      for (const process of this.processes) {
        byId[process.id] = process;
      }

      return byId;
    },
    ...mapState("catalogs", {
      processes: (s) => s["macro/Carreteras/process"],
    }),
    ...mapPermissions("Roads", "sinisterroads"),
  },

  watch: {
    "filterData.searchQuery": debounce(function () {
      this.currentPage = 1;
      this.fetchSinisters().then(() => {
        if (this.$refs.sinistersTable) {
          this.$refs.sinistersTable.focusSearch();
        }
      });
    }, 500),
    filtersParams: debounce(function () {
      this.fetchSinisters();
    }, 500),
    "paginationInfo.previous": function (value) {
      this.currentPage = (value || 0) + 1;
    },
    currentPageSize() {
      this.currentPage = 1;
    },
    paginationInfo(value) {
      this.$emit("update:paginationInfo", value);
    },
  },

  methods: {
    editRoadSinister(roadSinister) {
      const routeData = this.$router.resolve({
        name: "Editar Siniestro Carretero",
        params: {
          id: roadSinister.id,
        },
      });
      window.open(routeData.href, "_blank");
    },
    addRoadSinister() {
      this.$router.push({ path: "/siniestros/carreteros/nuevo" });
    },
    goToSinisterFile(roadSinister) {
      const routeData = this.$router.resolve({
        name: "Ficha de Siniestro Carretero",
        params: {
          id: roadSinister.id,
        },
      });
      window.open(routeData.href, "_blank");
    },
    fetchSinisters() {
      this.loading = true;
      this.roadSinisters = [];
      return sinisterService
        .fetch(this.fetchParams)
        .then(({ sinisters, paginationInfo }) => {
          this.roadSinisters = sinisters;
          this.paginationInfo = paginationInfo;
        })
        .catch(this.$fireErrorResponse)
        .finally(() => (this.loading = false));
    },
    downloadReport() {
      const {
        from: fromP,
        to: toP,
        highway,
        job: sinisterType,
      } = this.filtersParams;
      const from = fromP ? moment(fromP).format("YYYY-MM-DD") : undefined;
      const to = toP ? moment(toP).format("YYYY-MM-DD") : undefined;
      const highways =
        highway && highway.length ? highway.join(",") : undefined;
      downloadFile("sinisters/sinister_download_report/", "reporte.xls", {
        config: {
          params: {
            type: "xls",
            fromDate: from,
            toDate: to,
            highway: highways,
            sinisterType: sinisterType ? sinisterType : undefined,
          },
        },
      })
        .then(() => {
          this.$notify({
            message: "Descargando archivo.",
            icon: "close",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "success",
          });
        })
        .catch(this.$fireErrorResponse);
    },
    updateFilterData(item, value) {
      this.currentPage = 1;
      this.filterData[item] = value;
    },

    processClass(process) {
      const colorKey = (process.color || "red").replace(" ", "-");
      return `macro-${colorKey}`;
    },

    sinisterClass(sinister) {
      const process = this.processesById[sinister.processId];
      if (!process) {
        return "";
      }
      return this.processClass(process);
    },
    updateCrolAdvances() {
      this.$loadingMessage();
      sinisterService
        .updateAdvanceStatusCROL()
        .then(({ succes }) => {
          this.$successMessage({ title: succes || "Anticipos actualizados" });
        })
        .catch(this.$fireErrorResponse);
    },
  },
};
</script>

<style lang="css" scoped>
.urgent-style {
  color: red !important;
}
.priority-style {
  color: orange !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/RoadSinisters/Index.vue